/**
 * UWB Tag Manager Component
 * 
 * Allows admins to:
 * - Add new UWB tags (forklifts)
 * - View tag status
 * - Link tags to assets
 * 
 * Note: UWB readings are now ingested via backend API.
 * Serial/COM port configuration has been removed.
 */

import { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';

// Use the same API URL logic as api.js
const API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? 'http://localhost:5001' : '');
const TOKEN_KEY = 'asset_track_token'; // Match the token key from api.js

export default function UWBTagManager({ warehouseId, onClose }) {
  const { isDark } = useTheme();
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingTag, setEditingTag] = useState(null);

  // Fetch tags
  const fetchTags = async () => {
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const url = warehouseId 
        ? `${API_URL}/api/uwb/tags?warehouseId=${warehouseId}`
        : `${API_URL}/api/uwb/tags`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setTags(data.tags || []);
      }
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  };

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchTags();
      setLoading(false);
    };
    loadData();

    // Refresh tags every 10 seconds
    const interval = setInterval(fetchTags, 10000);
    return () => clearInterval(interval);
  }, [warehouseId]);

  if (loading) {
    return (
      <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50`}>
        <div className={`${isDark ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'} rounded-lg p-6`}>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`${isDark ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'} rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col`}>
        {/* Header */}
        <div className="p-6 border-b border-gray-600">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold">UWB Tag Management</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white text-2xl"
            >
              ×
            </button>
          </div>
          <p className="text-sm opacity-70 mt-2">
            Manage UWB tags (forklifts) for real-time tracking. UWB readings are ingested via backend API.
          </p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Add Tag Button */}
          <div className="mb-6">
            <button
              onClick={() => setShowAddModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              + Add New Tag
            </button>
            <button
              onClick={fetchTags}
              className="ml-3 px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
            >
              🔄 Refresh
            </button>
          </div>

          {/* Info Box */}
          <div className="mb-6 p-4 bg-blue-500 bg-opacity-10 border border-blue-500 rounded-lg">
            <h3 className="font-bold mb-2">📡 Backend Integration</h3>
            <p className="text-sm opacity-90">
              UWB distance readings are received via the backend API endpoint:
            </p>
            <code className="block mt-2 p-2 bg-black bg-opacity-20 rounded text-xs">
              POST {API_URL || 'https://aegisassettracking.onrender.com'}/api/uwb/readings
            </code>
            <p className="text-xs opacity-70 mt-2">
              External systems (Raspberry Pi gateway, etc.) should POST readings to this endpoint.
            </p>
          </div>

          {/* Tags List */}
          <div className="space-y-4">
            {tags.length === 0 ? (
              <p className="text-center opacity-70 py-8">No tags configured yet</p>
            ) : (
              tags.map((tag) => (
                <TagCard
                  key={tag.tag_id}
                  tag={tag}
                  isDark={isDark}
                  onEdit={() => setEditingTag(tag)}
                />
              ))
            )}
          </div>
        </div>

        {/* Add Tag Modal */}
        {showAddModal && (
          <AddTagModal
            warehouseId={warehouseId}
            onClose={() => setShowAddModal(false)}
            onSuccess={() => {
              setShowAddModal(false);
              fetchTags();
            }}
            isDark={isDark}
          />
        )}

        {/* Edit Tag Modal */}
        {editingTag && (
          <EditTagModal
            tag={editingTag}
            onClose={() => setEditingTag(null)}
            onSuccess={() => {
              setEditingTag(null);
              fetchTags();
            }}
            isDark={isDark}
          />
        )}
      </div>
    </div>
  );
}

// Tag Card Component
function TagCard({ tag, isDark, onEdit }) {
  const isActive = tag.status === 'active';
  const hasRecentPosition = tag.position_timestamp && 
    (new Date() - new Date(tag.position_timestamp)) < 60000; // Within last minute
  const metadata = tag.metadata || {};
  const piIp = metadata.pi_ip;

  return (
    <div className={`p-4 border rounded-lg ${isDark ? 'border-gray-600 bg-gray-700' : 'border-gray-300 bg-gray-50'}`}>
      <div className="flex justify-between items-start mb-3">
        <div>
          <h3 className="font-bold text-lg">{tag.tag_name}</h3>
          <p className="text-sm opacity-70">Tag ID: {tag.tag_hardware_id}</p>
          {tag.asset_name && (
            <p className="text-sm opacity-70">Linked to: {tag.asset_name}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onEdit}
            className={`px-3 py-1 rounded text-sm ${
              isDark
                ? 'bg-gray-600 hover:bg-gray-500 text-white'
                : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
            }`}
            title="Edit tag"
          >
            ✏️ Edit
          </button>
          <div className={`px-3 py-1 rounded-full text-sm ${
            hasRecentPosition
              ? 'bg-green-500 bg-opacity-20 text-green-500' 
              : isActive
              ? 'bg-yellow-500 bg-opacity-20 text-yellow-500'
              : 'bg-gray-500 bg-opacity-20 text-gray-500'
          }`}>
            {hasRecentPosition ? '● Tracking' : isActive ? '○ Active' : '○ Inactive'}
          </div>
        </div>
      </div>

      {/* Position Info */}
      {tag.position && (
        <div className="mt-3 p-3 bg-blue-500 bg-opacity-10 border border-blue-500 rounded text-sm">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <strong>Position:</strong> ({tag.position.x?.toFixed(1)}, {tag.position.y?.toFixed(1)})
            </div>
            <div>
              <strong>Confidence:</strong> {(tag.confidence * 100)?.toFixed(0)}%
            </div>
            <div>
              <strong>Hubs:</strong> {tag.hub_count || 0}
            </div>
            <div>
              <strong>Last Seen:</strong> {tag.last_seen_at ? new Date(tag.last_seen_at).toLocaleTimeString() : 'Never'}
            </div>
          </div>
          {piIp && (
            <div className="mt-2 text-xs opacity-70">
              📹 Camera IP: {piIp}:8080/stream-full
            </div>
          )}
        </div>
      )}

      {/* No Position Info */}
      {!tag.position && (
        <div className="mt-3 p-3 bg-gray-500 bg-opacity-10 border border-gray-500 rounded text-sm opacity-70">
          <p>⏳ Waiting for position data from backend...</p>
          <p className="text-xs mt-1">
            Ensure UWB readings are being sent to the backend API endpoint.
          </p>
        </div>
      )}
    </div>
  );
}

// Add Tag Modal
function AddTagModal({ warehouseId, onClose, onSuccess, isDark }) {
  const [tagId, setTagId] = useState('');
  const [name, setName] = useState('');
  const [piIp, setPiIp] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const response = await fetch(`${API_URL}/api/uwb/tags`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          tag_id: tagId,
          name: name,
          warehouse_id: warehouseId,
          metadata: piIp ? { pi_ip: piIp.trim(), stream_path: '/stream-full' } : { stream_path: '/stream-full' }
        })
      });

      if (response.ok) {
        onSuccess();
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to create tag');
      }
    } catch (err) {
      setError('Failed to create tag');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`${isDark ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'} rounded-lg shadow-xl p-6 w-full max-w-md`}>
        <h2 className="text-xl font-bold mb-4">Add New UWB Tag</h2>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">
              Tag ID <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={tagId}
              onChange={(e) => setTagId(e.target.value)}
              placeholder="FORKLIFT_001"
              className={`w-full px-3 py-2 border rounded-md ${
                isDark 
                  ? 'bg-gray-700 border-gray-600 text-white' 
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
              required
            />
            <p className="text-xs mt-1 opacity-70">
              Unique identifier for this tag (must match tag_id in API readings)
            </p>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Forklift 1"
              className={`w-full px-3 py-2 border rounded-md ${
                isDark 
                  ? 'bg-gray-700 border-gray-600 text-white' 
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
              required
            />
            <p className="text-xs mt-1 opacity-70">
              Friendly name for display
            </p>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">
              Raspberry Pi IP Address
            </label>
            <input
              type="text"
              value={piIp}
              onChange={(e) => setPiIp(e.target.value)}
              placeholder="192.168.1.100"
              className={`w-full px-3 py-2 border rounded-md ${
                isDark 
                  ? 'bg-gray-700 border-gray-600 text-white' 
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
            />
            <p className="text-xs mt-1 opacity-70">
              IP address of the Raspberry Pi running the camera server (e.g., 192.168.1.100)
              <br />
              Stream will be accessed at: http://[IP]:8080/stream-full
            </p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-500 bg-opacity-10 border border-red-500 rounded text-sm">
              {error}
            </div>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className={`flex-1 px-4 py-2 rounded-md border ${
                isDark
                  ? 'border-gray-600 hover:bg-gray-700'
                  : 'border-gray-300 hover:bg-gray-100'
              }`}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Creating...' : 'Create Tag'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Edit Tag Modal
function EditTagModal({ tag, onClose, onSuccess, isDark }) {
  const [name, setName] = useState(tag.tag_name || '');
  const [piIp, setPiIp] = useState((tag.metadata && tag.metadata.pi_ip) || '');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const token = localStorage.getItem(TOKEN_KEY);
      
      // Get existing metadata and update it
      const existingMetadata = tag.metadata || {};
      const updatedMetadata = {
        ...existingMetadata,
        pi_ip: piIp.trim() || undefined,
        stream_path: '/stream-full'
      };
      
      // Remove pi_ip if empty
      if (!piIp.trim()) {
        delete updatedMetadata.pi_ip;
      }

      // The view returns t.id as tag_id (database UUID), and t.tag_id as tag_hardware_id
      // So tag.tag_id is the database UUID we need for the PUT endpoint
      const tagDbId = tag.tag_id;
      
      if (!tagDbId) {
        setError('Tag ID not found. Cannot update tag.');
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_URL}/api/uwb/tags/${tagDbId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: name,
          metadata: updatedMetadata
        })
      });

      if (response.ok) {
        onSuccess();
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to update tag');
      }
    } catch (err) {
      setError('Failed to update tag');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`${isDark ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'} rounded-lg shadow-xl p-6 w-full max-w-md`}>
        <h2 className="text-xl font-bold mb-4">Edit UWB Tag</h2>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">
              Tag ID
            </label>
            <input
              type="text"
              value={tag.tag_hardware_id || tag.tag_id}
              disabled
              className={`w-full px-3 py-2 border rounded-md ${
                isDark 
                  ? 'bg-gray-700 border-gray-600 text-gray-400' 
                  : 'bg-gray-100 border-gray-300 text-gray-500'
              }`}
            />
            <p className="text-xs mt-1 opacity-70">
              Tag ID cannot be changed
            </p>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Forklift 1"
              className={`w-full px-3 py-2 border rounded-md ${
                isDark 
                  ? 'bg-gray-700 border-gray-600 text-white' 
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
              required
            />
            <p className="text-xs mt-1 opacity-70">
              Friendly name for display
            </p>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">
              Raspberry Pi IP Address
            </label>
            <input
              type="text"
              value={piIp}
              onChange={(e) => setPiIp(e.target.value)}
              placeholder="192.168.1.100"
              className={`w-full px-3 py-2 border rounded-md ${
                isDark 
                  ? 'bg-gray-700 border-gray-600 text-white' 
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
            />
            <p className="text-xs mt-1 opacity-70">
              IP address of the Raspberry Pi running the camera server (e.g., 192.168.1.100)
              <br />
              Stream will be accessed at: http://[IP]:8080/stream-full
            </p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-500 bg-opacity-10 border border-red-500 rounded text-sm">
              {error}
            </div>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className={`flex-1 px-4 py-2 rounded-md border ${
                isDark
                  ? 'border-gray-600 hover:bg-gray-700'
                  : 'border-gray-300 hover:bg-gray-100'
              }`}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Updating...' : 'Update Tag'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
